import { saveNotificationPreferences } from '@store/user/services';
import { createSignal } from 'solid-js';

const BELL_SETTING = 'bellNotificationsEnabled';
const TOAST_SETTING = 'toastNotificationsEnabled';
const TOAST_POSITION_SETTING = 'toastPosition';

export const toastCornerOptions = [
  { label: 'Top left', value: 'top-left' },
  { label: 'Top right', value: 'top-right' },
  { label: 'Bottom left', value: 'bottom-left' },
  { label: 'Bottom right', value: 'bottom-right' },
] as const;

export type ToastCorner = (typeof toastCornerOptions)[number]['value'];

const defaultToastCorner: ToastCorner = 'top-right';

export type NotificationPreferences = {
  bellEnabled: boolean;
  toastEnabled: boolean;
  toastPosition: ToastCorner;
};

const [bellEnabled, setBellEnabled] = createSignal(false);
const [toastEnabled, setToastEnabled] = createSignal(true);
const [toastPosition, setToastPosition] =
  createSignal<ToastCorner>(defaultToastCorner);
const [preferencesReady, setPreferencesReady] = createSignal(false);

export const bellNotificationsEnabled = bellEnabled;
export const toastNotificationsEnabled = toastEnabled;
export const toastNotificationPosition = toastPosition;
export const notificationPreferencesReady = preferencesReady;

const isToastCorner = (value: string): value is ToastCorner =>
  toastCornerOptions.some((option) => option.value === value);

const applyPreferences = (preferences: NotificationPreferences) => {
  setBellEnabled(preferences.bellEnabled);
  setToastEnabled(preferences.toastEnabled);
  setToastPosition(preferences.toastPosition);
};

const currentPreferences = (): NotificationPreferences => ({
  bellEnabled: bellEnabled(),
  toastEnabled: toastEnabled(),
  toastPosition: toastPosition(),
});

const settingValue = (
  settings: Record<string, string> | null | undefined,
  key: string,
) => {
  if (!settings) {
    return undefined;
  }
  const direct = settings[key];
  if (typeof direct === 'string') {
    return direct;
  }
  const match = Object.keys(settings).find(
    (candidate) => candidate.toLowerCase() === key.toLowerCase(),
  );
  const value = match ? settings[match] : undefined;
  return typeof value === 'string' ? value : undefined;
};

const settingEnabled = (
  settings: Record<string, string> | null | undefined,
  key: string,
  fallback: boolean,
) => {
  const value = settingValue(settings, key);
  if (value === undefined) {
    return fallback;
  }
  return value.toLowerCase() === 'true';
};

export const applyNotificationPreferencesFromSettings = (
  settings: Record<string, string> | null | undefined,
) => {
  const savedPosition = settingValue(settings, TOAST_POSITION_SETTING);
  applyPreferences({
    bellEnabled: settingEnabled(settings, BELL_SETTING, false),
    toastEnabled: settingEnabled(settings, TOAST_SETTING, true),
    toastPosition:
      savedPosition !== undefined && isToastCorner(savedPosition)
        ? savedPosition
        : defaultToastCorner,
  });
  setPreferencesReady(true);
};

let saveQueue: Promise<NotificationPreferences> | undefined;
let queuedSaveId = 0;
let lastSuccessfulSaveId = 0;

const mergePreferences = (
  settled: NotificationPreferences,
  patch: Partial<NotificationPreferences>,
): NotificationPreferences => ({
  bellEnabled: patch.bellEnabled ?? settled.bellEnabled,
  toastEnabled: patch.toastEnabled ?? settled.toastEnabled,
  toastPosition: patch.toastPosition ?? settled.toastPosition,
});

export const writeNotificationPreferences = (
  patch: Partial<NotificationPreferences>,
): Promise<void> => {
  const saveId = (queuedSaveId += 1);
  const prior = saveQueue ?? Promise.resolve(currentPreferences());
  const attempt = prior.then(async (settled) => {
    const next = mergePreferences(settled, patch);
    applyPreferences(next);
    try {
      await saveNotificationPreferences(next);
      lastSuccessfulSaveId = saveId;
      return next;
    } catch (error: unknown) {
      if (lastSuccessfulSaveId < saveId) {
        applyPreferences(settled);
      }
      throw error;
    }
  });

  saveQueue = attempt.then(
    (next) => next,
    () => currentPreferences(),
  );

  return attempt.then(() => undefined);
};
